import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, Icon, CircularProgress, Chip } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import Chart from "react-apexcharts";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import DialogShell from "./DialogShell";
import AlertTrendChart from "./AlertTrendChart";
import { useICCCColors, ICCC_COLORS as ICCC_FALLBACK, STATUS_FOR_PIE } from "../theme";
import { SEVERITY_LABEL, severityToHealth, HEALTH_RANK, categoryMeta } from "../../model/constants";
import {
  useGetActiveAlertsForAssetQuery,
  useGetAlertHistoryForAssetQuery,
  useClearCoordinateMutation,
} from "features/icccApiSlice";

const fmt = (ms) => {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const HealthChip = ({ health }) => {
  const color = ICCC_FALLBACK[health] || ICCC_FALLBACK.unknown;
  const label = { healthy: "Healthy", warning: "Warning", critical: "Critical", unknown: "Unknown" }[health] || "Unknown";
  return (
    <Chip
      size="small"
      label={label}
      sx={{ bgcolor: color, color: "#fff", fontWeight: 700, height: 22, fontSize: 11 }}
    />
  );
};
HealthChip.propTypes = { health: PropTypes.string };

const SeverityChip = ({ severity }) => {
  const color = ICCC_FALLBACK[severityToHealth(severity)] || ICCC_FALLBACK.unknown;
  return (
    <span style={{ background: color, color: "#fff", padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 600 }}>
      {SEVERITY_LABEL[Number(severity)] || "Unknown"}
    </span>
  );
};
SeverityChip.propTypes = { severity: PropTypes.number };

const Info = ({ label, value, colors }) => (
  <MDBox sx={{ minWidth: 0 }}>
    <MDTypography variant="caption" sx={{ color: colors.textMuted, display: "block", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </MDTypography>
    <MDTypography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
      {value || "—"}
    </MDTypography>
  </MDBox>
);
Info.propTypes = { label: PropTypes.string, value: PropTypes.node, colors: PropTypes.object };

const AlertTable = ({ rows, loading, showResolved, colors }) => {
  const th = {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: `2px solid ${colors.divider}`,
    fontWeight: 700,
    fontSize: 10.5,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: colors.textMuted,
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "7px 10px",
    borderBottom: `1px solid ${colors.divider}`,
    color: colors.textSecondary,
    verticalAlign: "middle",
  };
  if (loading) {
    return (
      <MDBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.accent }} />
      </MDBox>
    );
  }
  if (!rows.length) {
    return (
      <MDBox sx={{ textAlign: "center", color: colors.textMuted, py: 4 }}>No records</MDBox>
    );
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: 84 }} />
        <col />
        <col style={{ width: 128 }} />
        {showResolved && <col style={{ width: 128 }} />}
      </colgroup>
      <thead style={{ position: "sticky", top: 0, background: colors.cardBg2 }}>
        <tr>
          <th style={th}>Severity</th>
          <th style={th}>Message</th>
          <th style={th}>Raised</th>
          {showResolved && <th style={th}>Resolved</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((a) => (
          <tr
            key={a.id}
            style={{ transition: "background 100ms ease" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = colors.hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <td style={td}><SeverityChip severity={a.severity} /></td>
            <td style={{ ...td, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.message}>
              {a.message}
            </td>
            <td style={{ ...td, whiteSpace: "nowrap" }}>{fmt(a.raisedAt)}</td>
            {showResolved && <td style={{ ...td, whiteSpace: "nowrap" }}>{a.closedAt ? fmt(a.closedAt) : "—"}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
AlertTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  showResolved: PropTypes.bool,
  colors: PropTypes.object,
};

// Active-alerts-by-severity breakdown for this device — built from the same
// activeAlerts rows already fetched for the Active Alerts tab, no new data source.
const SeverityPie = ({ activeAlerts, colors, darkMode }) => {
  const slices = STATUS_FOR_PIE.map((p) => ({
    ...p,
    value: activeAlerts.filter((a) => a.health === p.key).length,
  })).filter((s) => s.value > 0);

  if (!slices.length) {
    return (
      <MDBox sx={{ textAlign: "center", color: colors.textMuted, py: 3 }}>
        No active alerts for this device
      </MDBox>
    );
  }

  const options = {
    chart: { type: "donut", toolbar: { show: false }, foreColor: colors.textSecondary },
    colors: slices.map((s) => s.color),
    labels: slices.map((s) => s.label),
    legend: { position: "right", fontSize: "11px", labels: { colors: colors.textSecondary } },
    stroke: { width: 2, colors: [colors.cardBg2] },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Number(val).toFixed(0)}%`,
      style: { fontSize: "10px", fontWeight: 700, colors: ["#fff"] },
    },
    plotOptions: { pie: { donut: { size: "45%" }, expandOnClick: false } },
    tooltip: { theme: darkMode ? "dark" : "light" },
  };

  return (
    <Chart
      options={options}
      series={slices.map((s) => s.value)}
      type="donut"
      height={180}
    />
  );
};
SeverityPie.propTypes = {
  activeAlerts: PropTypes.arrayOf(PropTypes.object).isRequired,
  colors: PropTypes.object.isRequired,
  darkMode: PropTypes.bool,
};

export default function DeviceDetailDialog({ device, open, onClose, editable }) {
  const ICCC_COLORS = useICCCColors();
  const [controller] = useMaterialUIController();
  const [tab, setTab] = useState(0);
  const assetId = device ? device.id ?? device.assetId : null;
  // Per-asset alert lookups route by UUID (numeric ids aren't accepted by the
  // telemetry alert endpoints) — assetId stays numeric for clearCoordinate +
  // the title, assetUuid is a separate value fed only to the alert queries.
  const assetUuid = device ? device.uuid : null;
  const alertSkip = !open || assetUuid == null;

  const activeQ = useGetActiveAlertsForAssetQuery(assetUuid, { skip: alertSkip });
  const historyQ = useGetAlertHistoryForAssetQuery(assetUuid, { skip: alertSkip });
  const [clearCoordinate, { isLoading: isUnplacing }] = useClearCoordinateMutation();

  const activeAlerts = useMemo(() => activeQ.data || [], [activeQ.data]);
  const history = useMemo(() => historyQ.data || [], [historyQ.data]);

  const trendAlerts = useMemo(() => {
    const byId = new Map();
    [...history, ...activeAlerts].forEach((a) => byId.set(a.id, a));
    return Array.from(byId.values());
  }, [history, activeAlerts]);

  const health = useMemo(() => {
    if (device?.health) return device.health;
    return activeAlerts.reduce(
      (worst, a) => (HEALTH_RANK[a.health] > HEALTH_RANK[worst] ? a.health : worst),
      "healthy"
    );
  }, [device, activeAlerts]);

  if (!device) return null;

  const name = device.nodeName || device.name || `Asset #${assetId}`;
  const meta = categoryMeta(device.category);
  const isPlaced = device.xCoordinate != null && device.yCoordinate != null;

  const handleUnplace = () => {
    clearCoordinate({ id: assetId })
      .unwrap()
      .then(() => onClose())
      .catch(() =>
        enqueueSnackbar("Failed to unplace device — please try again", {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        })
      );
  };

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title={name}
      subtitle={`${meta.label} • Asset #${assetId}`}
      icon={meta.icon}
      color={ICCC_COLORS[health]}
      maxWidth="md"
    >
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
          columnGap: 2.5,
          rowGap: 1.75,
          p: 2,
          mb: 2,
          borderRadius: 1.5,
          bgcolor: ICCC_COLORS.cardBg,
          border: `1px solid ${ICCC_COLORS.cardBorder}`,
        }}
      >
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textMuted, display: "block", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, mb: 0.5 }}>
            Status
          </MDTypography>
          <HealthChip health={health} />
        </MDBox>
        <Info label="Category" value={meta.label} colors={ICCC_COLORS} />
        <Info label="IP Address" value={device.ipAddress} colors={ICCC_COLORS} />
        <Info label="Location" value={device.location} colors={ICCC_COLORS} />
      </MDBox>

      {editable && isPlaced && (
        <MDBox sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <MDButton
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<Icon>link_off</Icon>}
            onClick={handleUnplace}
            disabled={isUnplacing}
          >
            {isUnplacing ? "Unplacing…" : "Unplace"}
          </MDButton>
        </MDBox>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 38,
          borderBottom: `1px solid ${ICCC_COLORS.divider}`,
          "& .MuiTab-root": {
            minHeight: 38,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12.5,
            color: ICCC_COLORS.textSecondary,
          },
          "& .MuiTab-root.Mui-selected": { color: ICCC_COLORS.accent },
          "& .MuiTabs-indicator": { backgroundColor: ICCC_COLORS.accent, height: 2.5 },
        }}
      >
        <Tab icon={<Icon fontSize="small">info</Icon>} iconPosition="start" label="Overview" />
        <Tab icon={<Icon fontSize="small">notifications_active</Icon>} iconPosition="start" label={`Active Alerts (${activeAlerts.length})`} />
        <Tab icon={<Icon fontSize="small">history</Icon>} iconPosition="start" label={`History (${history.length})`} />
        <Tab icon={<Icon fontSize="small">show_chart</Icon>} iconPosition="start" label="Trend" />
      </Tabs>

      <MDBox
        sx={{
          mt: 1,
          maxHeight: 320,
          overflow: "auto",
          border: `1px solid ${ICCC_COLORS.divider}`,
          borderRadius: 1,
          bgcolor: ICCC_COLORS.cardBg2,
        }}
      >
        {tab === 0 && (
          <MDBox sx={{ p: 2 }}>
            <MDBox
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
                mb: 2.5,
              }}
            >
              <Info label="Device Name" value={name} colors={ICCC_COLORS} />
              <Info label="IP Address" value={device.ipAddress} colors={ICCC_COLORS} />
              <Info label="MAC Address" value={device.macAddress} colors={ICCC_COLORS} />
              <Info label="Category" value={meta.label} colors={ICCC_COLORS} />
              <Info label="Group" value={device.serverGroupId} colors={ICCC_COLORS} />
              <Info label="Location" value={device.location} colors={ICCC_COLORS} />
              <Info label="Last Seen" value={fmt(device.lastSeen)} colors={ICCC_COLORS} />
            </MDBox>
            <MDTypography
              variant="caption"
              sx={{ color: ICCC_COLORS.textMuted, display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}
            >
              Active Alerts by Severity
            </MDTypography>
            <SeverityPie activeAlerts={activeAlerts} colors={ICCC_COLORS} darkMode={controller?.darkMode} />
          </MDBox>
        )}
        {tab === 1 && <AlertTable rows={activeAlerts} loading={activeQ.isFetching} colors={ICCC_COLORS} />}
        {tab === 2 && <AlertTable rows={history} loading={historyQ.isFetching} showResolved colors={ICCC_COLORS} />}
        {tab === 3 && <AlertTrendChart alerts={trendAlerts} />}
      </MDBox>
    </DialogShell>
  );
}

DeviceDetailDialog.propTypes = {
  device: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  editable: PropTypes.bool,
};

DeviceDetailDialog.defaultProps = {
  editable: false,
};
